import { slugify } from "@/utils/strings";

/** Maps questionnaire field paths to stable E2E test ids. */
const FIELD_TEST_IDS: Record<string, string> = {
  identifyingInformationFirstName: "intake-identifying-information-first-name",
  identifyingInformationLastName: "intake-identifying-information-last-name",
  identifyingInformationDOB: "intake-identifying-information-dob",
  demographicLegalSex: "intake-demographic-legal-sex",
  contactInformationAddress: "intake-contact-information-address",
  contactInformationCity: "intake-contact-information-city",
  contactInformationZipCode: "intake-contact-information-zip-code",
  contactInformationState: "intake-contact-information-state",
  billingInformationPaymentMethod: "intake-billing-information-payment-method",
  billingInformationHealthInsurance: "intake-billing-information-health-insurance",
  billingInformationSecondaryHealthInsurance:
    "intake-billing-information-secondary-health-insurance",
  billingInformationThirdHealthInsurance: "intake-billing-information-third-health-insurance",
  backgroundInformationSpiritualCultureProhibitions:
    "intake-background-information-spiritual-culture-prohibitions",
  backgroundInformationSpiritualCultureAcceptBloodProducts:
    "intake-background-information-spiritual-culture-accept-blood-products",
  backgroundInformationOccupationAndEducationOccupation: "intake-background-information-occupation",
  backgroundInformationOccupationAndEducationGrade: "intake-background-information-education-grade",
  supportNetworkPartnerExists: "intake-support-network-partner-exists",
  generalHealthHeightFt: "intake-general-health-height-ft",
  generalHealthHeightIn: "intake-general-health-height-in",
  generalHealthPrePregnancyWeight: "intake-general-health-pre-pregnancy-weight",
  generalHealthMedicationsOccurrence: "intake-general-health-medications-occurrence",
  generalHealthAllergyOccurrence: "intake-general-health-allergy-occurrence",
  generalHealthMedicationsFormsetTitle: "intake-general-health-medication-title",
  generalHealthMedicationsFormsetDose: "intake-general-health-medication-dose",
  generalHealthMedicationsFormsetComments: "intake-general-health-medication-comments",
  generalHealthAllergiesDetails: "intake-general-health-allergy-details",
  generalHealthAllergiesReaction: "intake-general-health-allergy-reaction",
  generalHealthAllergiesComments: "intake-general-health-allergy-comments",
  generalHealthMedicalProblemsRespiratory: "intake-general-health-medical-problems-respiratory",
  generalHealthMedicalProblemsRespiratoryFormsetCode:
    "intake-general-health-medical-problems-respiratory-code",
  generalHealthMedicalProblemsRespiratoryFormsetComments:
    "intake-general-health-medical-problems-respiratory-comments",
  gynecologicalHealthLastPapTestDate: "intake-gynecological-last-pap-test-date",
  lifestyleAlcohol: "intake-lifestyle-alcohol",
  lifestyleSmokingEver: "intake-lifestyle-smoking-ever",
  lifestyleSmoking: "intake-lifestyle-smoking",
  lifestyleDrugsOther: "intake-lifestyle-drugs-other",
  lifestyleSmokingHousehold: "intake-lifestyle-smoking-household",
  lifestyleSodaFrequency: "intake-lifestyle-soda-frequency",
  lifestyleCaffeineFrequency: "intake-lifestyle-caffeine-frequency",
  lifestyleProcessedFoodsFrequency: "intake-lifestyle-processed-foods-frequency",
  lifestyleExercise: "intake-lifestyle-exercise",
  lifestyleStressFeelings: "intake-lifestyle-stress-feelings",
  lifestyleStressFinancial: "intake-lifestyle-stress-financial",
  lifestyleSafetyEmotionalAbuse: "intake-lifestyle-safety-emotional-abuse",
  lifestyleSafetyFear: "intake-lifestyle-safety-fear",
  lifestyleSafetySexualAbuse: "intake-lifestyle-safety-sexual-abuse",
  lifestyleSafetyPhysicalAbuse: "intake-lifestyle-safety-physical-abuse",
  currentPregnancyLastMenstrualPeriod: "intake-current-pregnancy-last-menstrual-period",
  pastPregnanciesOccurrences: "intake-past-pregnancies-occurrences",
  familyMedicalHistoryProblems: "intake-family-medical-history-problems",
  familyMedicalHistoryProblemsFormsetCode: "intake-family-medical-history-problem-code",
  familyMedicalHistoryProblemsFormsetFamilyMember:
    "intake-family-medical-history-problem-family-member",
  familyMedicalHistoryProblemsFormsetComments: "intake-family-medical-history-problem-comments",
  "fhrBPM.fhrBPMLow": "labor-fhr-bpm-low",
  "fhrBPM.fhrBPMHigh": "labor-fhr-bpm-high",
  fhrDurationOfListening: "labor-fhr-listening-duration",
  fhrListeningDevice: "labor-fhr-listening-device",
  fhrRelationToContractions: "labor-fhr-relation-to-contractions",
  accelerationsDetected: "labor-fhr-accelerations-detected",
  decelerationsDetected: "labor-fhr-decelerations-detected",
  assessBaseline: "labor-fhr-assess-baseline",
  fhrBaseline: "labor-fhr-baseline-fhr",
  fhrDurationOfListeningFHT: "labor-fhr-baseline-listening-duration",
  fhrNotes: "labor-fhr-notes",
  "bloodPressure.systolic": "labor-vitals-systolic",
  "bloodPressure.diastolic": "labor-vitals-diastolic",
  pulse: "labor-vitals-pulse",
  "temperature.temperatureValue": "labor-vitals-temperature",
  "temperature.temperatureUnit": "labor-vitals-temperature-unit",
  temperatureUnit: "labor-vitals-temperature-unit",
  method: "labor-vitals-method",
  notes: "labor-vitals-notes",
  contractionsFrequency: "labor-contractions-frequency",
  contractionsDuration: "labor-contractions-duration",
  contractionStrength: "labor-contractions-strength",
  contractionsCoping: "labor-contractions-coping",
  contractionsNotes: "labor-contractions-notes",
  cervixDilation: "labor-cervical-dilation",
  cervixEffacement: "labor-cervical-effacement",
  cervixStation: "labor-cervical-station",
  cervixConsistency: "labor-cervical-consistency",
  cervixLocationOfCervix: "labor-cervical-location",
  cervixFetalPresentation: "labor-cervical-fetal-presentation",
  cervixActiveLabor: "labor-cervical-active-labor",
  cervixNotes: "labor-cervical-notes",
  romMethod: "labor-rom-method",
  romConfirmedBy: "labor-rom-confirmed-by",
  romConfirmedByOther: "labor-rom-confirmed-by-other",
  romFluidColor: "labor-rom-fluid-color",
  romFluidColorOther: "labor-rom-fluid-color-other",
  romNotes: "labor-rom-notes",
  clientPosition: "labor-position-client-position",
  clientInWater: "labor-position-client-in-water",
  clientInWaterOther: "labor-position-client-in-water-other",
  positionNotes: "labor-position-notes",
  assessmentNoteNotes: "labor-assessment-note-notes",
  membranesFluidColor: "labor-membranes-fluid-color",
  membranesFluidColorOther: "labor-membranes-fluid-color-other",
  membranesPooling: "labor-membranes-pooling",
  membranesNitrazine: "labor-membranes-nitrazine",
  membranesFernResults: "labor-membranes-fern-results",
  membranesNotes: "labor-membranes-notes",
  inputOutputInput: "labor-input-output-input",
  inputOutputOutput: "labor-input-output-output",
  inputOutputNotes: "labor-input-output-notes",
  medsAndTherapiesMedicationsFormsetMedication: "labor-meds-medication",
  medsAndTherapiesMedicationsFormsetForm: "labor-meds-form",
  medsAndTherapiesMedicationsFormsetDosage: "labor-meds-dosage",
  medsAndTherapiesMedicationsFormsetFrequency: "labor-meds-frequency",
  medsAndTherapiesMedicationsFormsetDuration: "labor-meds-duration",
  medsAndTherapiesMedicationsFrequencyDurationXDays: "labor-meds-duration-days",
  medsAndTherapiesMedicationsFormsetSpecialInstructions: "labor-meds-special-instructions",
  medsAndTherapiesMedicationsFormsetPRNReason: "labor-meds-prn-reason",
  medsAndTherapiesTherapiesFormsetTherapy: "labor-therapies-therapy",
  medsAndTherapiesTherapiesReason: "labor-therapies-reason",
  medsAndTherapiesTherapiesReasonOther: "labor-therapies-reason-other",
  medsAndTherapiesTherapiesIVAccess: "labor-therapies-iv-access",
  medsAndTherapiesTherapiesFluidVolume: "labor-therapies-fluid-volume",
  medsAndTherapiesNotes: "labor-meds-therapies-notes",
  locationSite: "labor-location-site",
  locationSiteOther: "labor-location-site-other",
  locationNotes: "labor-location-notes",
  admitDischargeType: "labor-admit-discharge-type",
  admitDischargeNotes: "labor-admit-discharge-notes",
  transportType: "labor-transport-type",
  transportProvider: "labor-transport-provider",
  transportContact: "labor-transport-contact",
  transportReasonIntrapartum: "labor-transport-reason-intrapartum",
  transportReasonIntrapartumOther: "labor-transport-reason-intrapartum-other",
  transportFrom: "labor-transport-from",
  transportHospital: "labor-transport-hospital",
  transportHospitalContact: "labor-transport-hospital-contact",
  transportCondition: "labor-transport-condition",
  transportFirstCall: "labor-transport-first-call",
  transportEMSArrived: "labor-transport-ems-arrived",
  transportTimeLeft: "labor-transport-time-left",
  transportArrivedAtHospital: "labor-transport-arrived-at-hospital",
  transportNotes: "labor-transport-notes",
  estimatedBloodLossCapture: "labor-ebl-capture",
  estimatedBloodLossEstimated: "labor-ebl-estimated",
  estimatedBloodLossQuantitative: "labor-ebl-quantitative",
  estimatedBloodLossNotes: "labor-ebl-notes",
  shoulderDystociaDate: "labor-shoulder-dystocia-date",
  shoulderDystociaTime: "labor-shoulder-dystocia-time",
  shoulderDystociaDuration: "labor-shoulder-dystocia-duration",
  shoulderDystociaType: "labor-shoulder-dystocia-type",
  shoulderDystociaNotes: "labor-shoulder-dystocia-notes",
  postpartumHemorrhageDate: "labor-pph-date",
  postpartumHemorrhageTime: "labor-pph-time",
  postpartumHemorrhageDuration: "labor-pph-duration",
  postpartumHemorrhageManeuversMedicine: "labor-pph-maneuver",
  postpartumHemorrhageNotes: "labor-pph-notes",
  resuscitationNotes: "labor-resuscitation-notes",
  resuscitationStimulation: "labor-resuscitation-stimulation",
  cptCode: "encounter-procedure-code-input",
  "birthingDateTime.birthingDate": "labor-birthing-date",
  "birthingDateTime.birthingTime": "labor-birthing-time",
  birthingCrownDateTime: "labor-birthing-crown-delivered",
  birthingHeadDateTime: "labor-birthing-head-delivered",
  birthingBodyDateTime: "labor-birthing-body-delivered",
  birthingType: "labor-birthing-type",
  birthingPosition: "labor-birthing-position",
  birthingInWater: "labor-birthing-in-water",
  birthingInWaterOther: "labor-birthing-in-water-specify",
  birthingFetalPresentation: "labor-birthing-fetal-presentation",
  birthingDeliveryMethod: "labor-birthing-delivery-method",
  birthingDeliveryMethodOther: "labor-birthing-delivery-method-specify",
  birthingNotes: "labor-birthing-notes",
  // Newborn Postpartum — Inputs & Outputs
  inputOutputFeedingType: "newborn-postpartum-inputs-outputs-feeding-type",
  inputOutputFeedingSupplementAmount: "newborn-postpartum-inputs-outputs-supplement-amount",
  inputOutputFeedingLength: "newborn-postpartum-inputs-outputs-feeding-length",
  inputOutputFeedingBehavior: "newborn-postpartum-inputs-outputs-feeding-behavior",
  inputOutputFeedingSupplement: "newborn-postpartum-inputs-outputs-feeding-supplement",
  inputOutputFeedingSupplementAmountPumpedMilk:
    "newborn-postpartum-inputs-outputs-pumped-milk-amount",
  inputOutputFeedingSupplementAmountUnitPumpedMilk:
    "newborn-postpartum-inputs-outputs-pumped-milk-unit",
  inputOutputFeedingSupplementAverageAmountPumpedMilk:
    "newborn-postpartum-inputs-outputs-pumped-milk-average",
  inputOutputFeedingSupplementAmountDonorMilk:
    "newborn-postpartum-inputs-outputs-donor-milk-amount",
  inputOutputFeedingSupplementAmountUnitDonorMilk:
    "newborn-postpartum-inputs-outputs-donor-milk-unit",
  inputOutputFeedingSupplementAverageAmountDonorMilk:
    "newborn-postpartum-inputs-outputs-donor-milk-average",
  inputOutputFeedingSupplementAmountFormula: "newborn-postpartum-inputs-outputs-formula-amount",
  inputOutputFeedingSupplementAmountUnitFormula: "newborn-postpartum-inputs-outputs-formula-unit",
  inputOutputFeedingSupplementAverageAmountFormula:
    "newborn-postpartum-inputs-outputs-formula-average",
  inputOutputFeedingSupplementFormsetType:
    "newborn-postpartum-inputs-outputs-formset-supplement-type",
  inputOutputFormsetTypeFeedingSupplementAmountPumpedMilk:
    "newborn-postpartum-inputs-outputs-formset-pumped-milk-amount",
  inputOutputFormsetTypeFeedingAmountUnitPumpedMilk:
    "newborn-postpartum-inputs-outputs-formset-pumped-milk-unit",
  inputOutputFormsetTypeFeedingAverageAmountPumpedMilk:
    "newborn-postpartum-inputs-outputs-formset-pumped-milk-average",
  inputOutputFormsetTypeFeedingSupplementAmountDonorMilk:
    "newborn-postpartum-inputs-outputs-formset-donor-milk-amount",
  inputOutputFormsetTypeFeedingAmountUnitDonorMilk:
    "newborn-postpartum-inputs-outputs-formset-donor-milk-unit",
  inputOutputFormsetTypeFeedingAverageAmountDonorMilk:
    "newborn-postpartum-inputs-outputs-formset-donor-milk-average",
  inputOutputFormsetTypeFeedingSupplementAmountFormula:
    "newborn-postpartum-inputs-outputs-formset-formula-amount",
  inputOutputFormsetTypeFeedingAmountUnitFormula:
    "newborn-postpartum-inputs-outputs-formset-formula-unit",
  inputOutputFormsetTypeFeedingAverageAmountFormula:
    "newborn-postpartum-inputs-outputs-formset-formula-average",
  inputOutputVoiding: "newborn-postpartum-inputs-outputs-voiding",
  inputOutputVoidingNormal: "newborn-postpartum-inputs-outputs-voiding-normal-notes",
  inputOutputVoidingNotNormal: "newborn-postpartum-inputs-outputs-voiding-not-normal-notes",
  inputOutputBowel: "newborn-postpartum-inputs-outputs-bowel",
  inputOutputBowelNormal: "newborn-postpartum-inputs-outputs-bowel-normal-notes",
  inputOutputBowelNotNormal: "newborn-postpartum-inputs-outputs-bowel-not-normal-notes",
  "newbornPostpartumInputOutput.inputOutputNotes": "newborn-postpartum-inputs-outputs-notes",
  // Newborn Postpartum — Meds & Therapies
  newbornPostpartumMedicationFormsetTitle: "newborn-postpartum-medications-therapies-med-name",
  newbornPostpartumMedicationFormsetDose: "newborn-postpartum-medications-therapies-med-dose",
  newbornPostpartumMedicationFormsetRoute: "newborn-postpartum-medications-therapies-med-route",
  newbornPostpartumMedicationFormsetExpirationDate:
    "newborn-postpartum-medications-therapies-med-expiration",
  newbornPostpartumMedicationFormsetLotNumber: "newborn-postpartum-medications-therapies-med-lot",
  newbornPostpartumMedicationFormsetNotes: "newborn-postpartum-medications-therapies-med-notes",
  newbornPostpartumTherapiesFormsetName: "newborn-postpartum-medications-therapies-therapy-name",
  newbornPostpartumTherapiesFormsetNotes: "newborn-postpartum-medications-therapies-therapy-notes",
  newbornPostpartumMedsAndTherapiesGeneralNotes:
    "newborn-postpartum-medications-therapies-general-notes",
};

const FORMSET_ACTION_TEST_IDS: Record<string, string> = {
  cptDiagnosesFormset: "encounter-procedure-add-diagnosis-button",
  generalHealthMedicationsFormset: "intake-general-health-add-medication-row",
  generalHealthAllergiesFormset: "intake-general-health-add-allergy-row",
  generalHealthMedicalProblemsRespiratoryFormset:
    "intake-general-health-add-respiratory-problem-row",
  familyMedicalHistoryProblemsFormset: "intake-family-history-add-problem-row",
  inputOutputFeedingSupplementFormset: "newborn-postpartum-inputs-outputs-add-supplement-row",
  newbornPostpartumMedicationFormset: "newborn-postpartum-medications-therapies-add-med-row",
  newbornPostpartumTherapiesFormset: "newborn-postpartum-medications-therapies-add-therapy-row",
  medsAndTherapiesMedicationsFormset: "labor-meds-add-medication-row",
  medsAndTherapiesTherapiesFormset: "labor-therapies-add-therapy-row",
  shoulderDystociaManeuversFormset: "labor-shoulder-dystocia-add-maneuver",
  postpartumHemorrhageManeuversFormset: "labor-pph-add-maneuver",
};

export function questionnaireFieldTestId(formFieldName: string): string | undefined {
  if (FIELD_TEST_IDS[formFieldName]) {
    return FIELD_TEST_IDS[formFieldName];
  }

  const pathSegments = formFieldName.split(".");
  const leafLinkId = pathSegments.at(-1);
  if (leafLinkId && FIELD_TEST_IDS[leafLinkId]) {
    return FIELD_TEST_IDS[leafLinkId];
  }

  if (pathSegments.length >= 2) {
    const nestedLinkId = pathSegments.slice(-2).join(".");
    if (FIELD_TEST_IDS[nestedLinkId]) {
      return FIELD_TEST_IDS[nestedLinkId];
    }
  }

  if (formFieldName.endsWith(".cptDiagnosesFormsetDiagnosis")) {
    return "encounter-procedure-diagnosis-input";
  }

  if (formFieldName.endsWith(".cptDiagnosesFormsetAddToProblemList")) {
    return "encounter-procedure-add-to-problem-list";
  }

  if (formFieldName.endsWith("pregnancyDetailsFormsetDate")) {
    return "gtpal-pregnancy-detail-date";
  }

  if (formFieldName.endsWith("pregnancyDetailsFormsetOutcome")) {
    return "gtpal-pregnancy-detail-outcome";
  }

  if (formFieldName.endsWith("pregnancyDetailsFormsetGestationalAge")) {
    return "gtpal-pregnancy-detail-gestational-age";
  }

  if (formFieldName.startsWith("results.") && leafLinkId?.endsWith("-result")) {
    return `manual-lab-result-${leafLinkId.slice(0, -"-result".length)}`;
  }

  return undefined;
}

export function questionnaireFormsetActionTestId(linkId: string): string | undefined {
  return FORMSET_ACTION_TEST_IDS[linkId];
}

export function questionnaireRadioOptionTestId(
  formFieldName: string,
  optionLabel: string,
): string | undefined {
  const fieldTestId = questionnaireFieldTestId(formFieldName);
  if (!fieldTestId) return undefined;
  return `${fieldTestId}-${slugify(optionLabel)}`;
}

export function questionnaireCheckboxOptionTestId(
  formFieldName: string,
  optionLabel: string,
): string | undefined {
  const fieldTestId = questionnaireFieldTestId(formFieldName);
  if (!fieldTestId) return undefined;
  return `${fieldTestId}-option-${slugify(optionLabel)}`;
}
